import React, { useEffect, useState } from 'react';
import {
  Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Select, Text, useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { NIL } from 'uuid';
import { useQueryClient } from 'react-query';
import useContact from '../../hooks/useContact';
import { CustomerStaffMember } from '../../models/contactInfo';
import { SalesBibleRow as Row } from '../../hooks/useSalesBible';

const apiUrl = (): string => process.env.REACT_APP_BASIX_API || '';

// item 1172 (Chad) — assign the CUSTOMER STAFF / PM straight from the Sales Bible. The
// proposal locks at approval (priced content), but the staff contact is job metadata —
// this modal writes through the dedicated endpoint that works regardless of lock. The
// roster is the SAME CRM staff list the proposal form uses; no overrides, no drift.
const AssignCustomerPmModal: React.FC<{ row: Row | null; onClose: () => void }> = ({ row, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: customer } = useContact({ id: row?.customerId || NIL });
  const staff: CustomerStaffMember[] = (customer?.staff ?? []) as CustomerStaffMember[];
  const [staffId, setStaffId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setStaffId(''); }, [row?.id]);

  const save = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const doc = row.kind === 'workOrder' ? 'WorkOrder' : 'Proposal';
      await axios.post(`${apiUrl()}${doc}/${row.id}/customer-pm`, { customerStaffId: staffId || null });
      queryClient.invalidateQueries('proposals');
      queryClient.invalidateQueries('workOrders');
      toast({ status: 'success', title: 'Customer staff/PM assigned.' });
      onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not assign', description: String(e?.response?.data ?? '') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={!!row} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Customer staff / PM — ${row?.jobName ?? ''}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" opacity={0.7} mb={3}>
            {`Pick from ${row?.integrator ?? 'the customer'}'s CRM staff roster — the same list the
            proposal form uses. Works even after the document locks.`}
          </Text>
          <Select
            placeholder={staff.length ? 'Choose a staff member…' : 'No staff on this customer yet — add them in CRM'}
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            isDisabled={staff.length === 0}
          >
            {staff.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="brandGreen" onClick={save} isLoading={saving} isDisabled={!staffId}>
            Assign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AssignCustomerPmModal;
